import React from 'react'
import { Table, Icon } from 'antd'
import { useTranslation } from 'react-i18next'
import FolderCard from './FolderCard'
import RowActions from './RowActions'
import { countFolderItems } from '../utils/folderTree'
import { formatDate } from '../utils/adapters'
import { translateTagName } from '../../campaignManager/hooks/contentManager/useLibraryContents'

// Renders the folders of the current level — as a grid of cards or, in list
// view, a selectable antd Table. Styling comes from the parent AdsSectionHolder
// (.ads-group / .folders-row / .folders-table / .cell-folder).
//
// File drag & drop is owned by the parent's single react-dropzone zone; folders
// only carry `data-folder-drop`/`data-folder-id` markers (so the drop event can
// resolve which folder was targeted) and highlight when `dragFolderId` matches.
const FoldersGroup = ({
  folders = [],
  viewMode = 'grid',
  selectedIds = [],
  onSelectionChange,
  onSelectFolder,
  dragFolderId = null,
  openModal,
}) => {
  const { t } = useTranslation()

  const isSelected = (folder) => selectedIds.includes(folder.id)

  const toggleSelect = (folder) => {
    if (!onSelectionChange) return
    const next = isSelected(folder)
      ? selectedIds.filter((id) => id !== folder.id)
      : [...selectedIds, folder.id]
    onSelectionChange(next)
  }

  // "Seleccionar carpeta" toggles the folder in the shared selection (so it
  // counts in the bulk bar). rename/delete open their modals; the backend
  // dispatch is commented out in AdLibraryModals (no endpoint yet).
  const getFolderMenuItems = (folder) => [
    {
      key: 'select',
      label: isSelected(folder) ? t('Deselect folder') : t('Select folder'),
      onClick: () => toggleSelect(folder),
    },
    { key: 'rename', label: t('Rename'), onClick: () => openModal('renameFolder', { folder }) },
    { key: 'delete', label: t('Archive'), onClick: () => openModal('deleteFolder', { folder }) },
  ]

  const columns = [
    {
      title: t('Folder'),
      key: 'name',
      render: (_, folder) => (
        <span className="cell-folder">
          <Icon type="folder" className="cell-folder-icon" />
          {translateTagName(folder.name, t)}
        </span>
      ),
    },
    { title: t('Items'), key: 'items', width: 110, render: (_, f) => countFolderItems(f) },
    { title: t('Creation date'), key: 'date', width: 160, render: (_, f) => formatDate(f.createdAt) },
    {
      title: t('Actions'),
      key: 'actions',
      width: 90,
      align: 'center',
      render: (_, f) => <RowActions items={getFolderMenuItems(f)} />,
    },
  ]

  if (!folders.length) return null

  return (
    <div className="ads-group">
      {viewMode === 'list' ? (
        <Table
          className="folders-table"
          rowKey="id"
          columns={columns}
          dataSource={Array.from(folders)}
          rowSelection={onSelectionChange ? { selectedRowKeys: Array.from(selectedIds), onChange: onSelectionChange } : undefined}
          pagination={false}
          size="middle"
          title={() => t('Folders')}
          // Folders carry a `children` array (subfolders); point antd's tree
          // detection at a non-existent key so it doesn't render expand icons —
          // subfolder navigation is handled by row click, not tree expansion.
          childrenColumnName="__noChildren"
          // Highlight the row a file is being dragged onto.
          rowClassName={(folder) => (dragFolderId === folder.id ? 'row-drag-over' : '')}
          onRow={(folder) => ({
            // Marker read by the parent drop zone to resolve the drop target.
            'data-folder-drop': '',
            'data-folder-id': folder.id,
            // Clicking the row opens the folder (navigates + injects into the
            // path), except when toggling the selection checkbox.
            onClick: (e) => {
              if (e.target.closest && e.target.closest('.ant-table-selection-column')) return
              onSelectFolder && onSelectFolder(folder)
            },
          })}
        />
      ) : (
        <div className="folders-row">
          {folders.map((folder) => (
            <FolderCard
              key={folder.id}
              folderId={folder.id}
              name={translateTagName(folder.name, t)}
              itemsCount={countFolderItems(folder)}
              selected={isSelected(folder)}
              dragOver={dragFolderId === folder.id}
              onClick={() => onSelectFolder && onSelectFolder(folder)}
              menuItems={getFolderMenuItems(folder)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default FoldersGroup
